import { useCallback, useSyncExternalStore } from 'react';
import { delegationTokenManager } from '@api/auth/delegationTokenManager';
import { buildOAuthAuthorizeUrl, delegationTokenConfig, logoutLandingConfig } from '../api/auth/config';
import { clearOAuthToken, isOAuthTokenValid, getStoredOAuthToken } from '../api/auth/oauthToken';
import { logoutFromTeamLocker } from '../api/auth/teamLockerSession';
import { getLogger } from '@logging/logger';

// Key for storing the return URL in sessionStorage
const RETURN_URL_KEY = 'oauth_return_url';

/**
 * Save the current URL to return to after authentication
 */
export const saveReturnUrl = () => {
  const currentUrl = window.location.pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
};

/**
 * Get and clear the saved return URL
 * @returns The saved return URL or '/' if none was saved
 */
export const getAndClearReturnUrl = (): string => {
  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
  sessionStorage.removeItem(RETURN_URL_KEY);
  return returnUrl || '/';
};

// Simple store for auth state changes
const authListeners = new Set<() => void>();
const notifyAuthChange = () => {
  for (const listener of authListeners) {
    listener();
  }
};

// Wire up delegationTokenManager to notify auth changes when OAuth token becomes invalid
delegationTokenManager.setAuthRequiredCallback(notifyAuthChange);

// Subscribe/getSnapshot for useSyncExternalStore
const subscribe = (listener: () => void) => {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
};

const getSnapshot = () => isOAuthTokenValid();

interface UseAuthReturn {
  /** Whether the user has a valid OAuth token */
  isAuthenticated: boolean;
  /** The current OAuth token data (if authenticated) */
  oauthToken: ReturnType<typeof getStoredOAuthToken>;
  /** Redirect to OAuth login page */
  login: () => void;
  /** Clear all tokens and optionally redirect to login */
  logout: (redirect?: boolean) => void;
  /** Force refresh the delegation token */
  refreshDelegationToken: () => Promise<void>;
  /** Clear the delegation token cache */
  clearDelegationTokenCache: () => void;
}
export const logoutUser = async (redirect: boolean = true) => {
  // End the server session — the API clears the shared OAuth cookie
  // (OAUTH_TOKEN_NAME, from PUBLIC_OAUTH_COOKIE_NAME) for every app at once.
  // Awaited so the redirect below doesn't cancel it.
  // Do NOT redirect to another app's logout page here: this page is itself a
  // hop in the cross-app logout chain, and bouncing back into it would loop.
  await logoutFromTeamLocker();

  // Clear delegation token
  delegationTokenManager.clearToken();

  // Client-side fallback cookie removal + legacy localStorage cleanup
  clearOAuthToken();
  // Notify listeners of auth state change
  notifyAuthChange();

  if (!redirect) return;

  if (logoutLandingConfig.url && logoutLandingConfig.url !== '') {
    window.location.href = logoutLandingConfig.url;
  }
};
/**
 * Hook for managing OAuth authentication and delegation tokens
 */
export const useAuth = (): UseAuthReturn => {
  const isAuthenticated = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const login = useCallback(() => {
    // Save current URL to return to after authentication
    saveReturnUrl();
    const authorizeUrl = buildOAuthAuthorizeUrl();
    window.location.href = authorizeUrl;
  }, []);

  const logout = useCallback((redirect: boolean = true) => {
    logoutUser(redirect);
  }, []);

  const refreshDelegationToken = useCallback(async () => {
    try {
      delegationTokenManager.clearToken();
      await delegationTokenManager.getToken();
    } catch (error) {
      getLogger().error('manual token refresh failed', {}, error);
      throw error;
    }
  }, []);

  const clearDelegationTokenCache = useCallback(() => {
    delegationTokenManager.clearToken();
  }, []);

  return {
    isAuthenticated,
    oauthToken: getStoredOAuthToken(),
    login,
    logout,
    refreshDelegationToken,
    clearDelegationTokenCache,
  };
};

/**
 * Notify the auth system that a re-authentication is needed
 * Called by delegationTokenManager when OAuth token is invalid
 */
export const triggerAuthChange = notifyAuthChange;

export const verifySession = async (): Promise<void> => {
  if (!isOAuthTokenValid()) {
    logoutUser(); // Force redirect to login
    return;
  }

  try {
    const headers = typeof delegationTokenConfig.headers === 'function' ? delegationTokenConfig.headers() : delegationTokenConfig.headers;
    const response = await fetch(delegationTokenConfig.endpoint, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      delegationTokenManager.clearToken();
      clearOAuthToken();
      notifyAuthChange();
    }
  } catch {}
};
